<template>
  <div v-if="skeleton" class="rich-text loading">
    <div class="output-text mce-content">
      <cv-skeleton-text :paragraph="true" width="100%" />
    </div>
  </div>
  <div v-else class="rich-text loaded" :class="{ expandable: canToggle }">
    <div
      ref="container"
      v-safe-html="sanitizedContent"
      class="output-text mce-content rich-text-content"
      :class="cssClass"
    />
    <div class="rich-text-actions">
      <cv-link v-if="canToggle" class="toggle" @click="handleToggle">
        <span v-if="isExpanded" class="non-highlightable">{{ t("read_less") }} </span>
        <span v-else class="non-highlightable">{{ t("read_more") }} </span>
      </cv-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { createApp } from "vue";
import DOMUtils from "@utils/dom.utils";
import RichTextTooltip from "./RichTextTooltip.vue";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";
import { NAMESPACE } from "@modules/editor/service/editor.constant";
import htmlSanitizer from "@plugins/common/service/html.sanitizer";
import { useVm } from "@utils/vm.ts";
import { EXTRA_HEIGHT } from "@plugins/richtext/richtext.constant.ts";
import { useI18n } from "vue-i18n";
import { fnQ, loadWirisPlugin } from "@plugins/richtext/utils/wiris.utils.ts";

const props = withDefaults(
  defineProps<{
    visible?: boolean;
    expandable?: boolean;
    expanded?: boolean;
    skeleton?: boolean;
    truncate?: boolean;
    content?: string;
    truncationType?: string;
    config?: Record<string, unknown>;
    // eslint-disable-next-line vue/prop-name-casing
    dynamicMountChildMixin_parentComponent?: object;
    // eslint-disable-next-line vue/prop-name-casing
    dynamicMountChildMixin_isDynamicallyMounted?: boolean;
  }>(),
  {
    visible: true,
    expandable: true,
    expanded: true,
    skeleton: false,
    truncate: false,
    content: "",
    truncationType: "primary",
    config: () => ({}),
    dynamicMountChildMixin_parentComponent: () => ({}),
    dynamicMountChildMixin_isDynamicallyMounted: false,
  },
);

useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

const emit = defineEmits<(e: "ready") => void>();

const container = ref<HTMLElement | null>(null);
const isExpandable = ref<boolean>(props.expandable && !!props.content);
const isExpanded = ref(false);
const richTextReady = ref(false);
const richTextPluginReadyMap = ref<Record<string, boolean>>({});
const sanitizedContent = ref("");
const whitelistHtmlFetched = ref(false);

const { t } = useI18n();
const { vm } = useVm();
const store = useStore();
const getWhitelistHtmlTags = computed<string[] | undefined>(
  () => store.getters[`${NAMESPACE.APP}/getWhitelistHtmlTags`],
);

const cssClass = computed(() => {
  const isCollapsed = !isExpanded.value;
  return [
    {
      collapsed: canToggle.value && isCollapsed,
      truncate: props.truncate,
    },
    `${props.truncationType}-truncation`,
  ];
});

const canToggle = computed(() => props.expandable && isExpandable.value);

function processWiris(container, parent) {
  const pluginName = "wiris";
  const checkWirisLoaded = () => {
    if (
      window.com &&
      window.com.wiris &&
      window.com.wiris.js &&
      window.com.wiris.js.JsPluginViewer
    ) {
      window.com.wiris.js.JsPluginViewer.parseElement(container, true);
      parent.updatePluginReadyState(pluginName);
    } else {
      fnQ.push(checkWirisLoaded);
    }
  };
  parent.addPluginReadyState(pluginName);
  loadWirisPlugin(checkWirisLoaded);
}

function processTooltips(container: Element, parent: ParentApi) {
  const pluginName = "tooltip";
  const selector = ".custom-tooltip";
  DOMUtils.elOf(container, selector).forEach((element, index) => {
    const id = `${pluginName}_${index}`;

    const app = createApp(RichTextTooltip, {
      ...AppInstanceUtils.generateDynamicMountProps(vm),
      context: parent.richTextContext,
      label: element.innerHTML,
      tip: element.getAttribute("title"),
      id,
      onLoaded: parent.tooltipLoaded,
    });
    AppInstanceUtils.installCommonPlugin(app);
    parent.addPluginReadyState(id);

    const component = app.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(component);
  });
}

function processTable(container: Element) {
  const selector = "table";
  DOMUtils.elOf(container, selector).forEach((element) => {
    const tableWrapper = DOMUtils.createElement("div", { class: "table-wrapper" });
    DOMUtils.insertAfter(tableWrapper, element);
    const next = DOMUtils.nextElementByClass(element, "table-wrapper");
    if (next) {
      next.appendChild(element);
    }
  });
}

watch(
  () => props.visible,
  (nowVisible) => {
    if (!nowVisible) return;
    processContent();
    updateIsExpandable();
  },
);

onMounted(() => {
  if (!props.skeleton) {
    store.dispatch(`${NAMESPACE.APP}/fetchWhitelistHtmlTags`).then(() => {
      whitelistHtmlFetched.value = true;
      if (props.content) {
        sanitizedContent.value =
          htmlSanitizer.sanitizeHtmlForRichText(props.content, getWhitelistHtmlTags.value) ?? "";
      }
      processContent();
      updateIsExpandable();
      if (props.content) {
        sanitizedContent.value =
          htmlSanitizer.sanitizeHtmlForRichText(props.content, getWhitelistHtmlTags.value) ?? "";
      }
    });
  }
});

function processContent() {
  const el = container.value as Element;
  processTooltips(el, parentApi);
  processTable(el);
  processWiris(el, parentApi);
}

function updateIsExpandable() {
  if (isExpandable.value && isExpanded.value) return;
  calculateContentHeight();
  const el = container.value as Element;
  if (DOMUtils.elOf(el, "figure.media").length) {
    if (!props.truncate) {
      if (isExpandable.value) {
        isExpanded.value = true;
      }
    }
  }
}

function calculateContentHeight() {
  const el = container.value as HTMLElement;
  const clientHeight = el.clientHeight + EXTRA_HEIGHT;
  isExpandable.value = clientHeight < el.scrollHeight;
  isExpanded.value = props.expanded;
}

function handleToggle() {
  isExpanded.value = !isExpanded.value;
}

function tooltipLoaded(id: string) {
  updatePluginReadyState(id);
}

function addPluginReadyState(pluginName: string) {
  richTextPluginReadyMap.value = { ...richTextPluginReadyMap.value, [pluginName]: false };
}

function updatePluginReadyState(pluginName: string) {
  richTextPluginReadyMap.value = { ...richTextPluginReadyMap.value, [pluginName]: true };
  checkRichTextReady();
}

function checkRichTextReady() {
  const allReady = !Object.values(richTextPluginReadyMap.value).some((v) => !v);
  if (allReady && whitelistHtmlFetched.value) {
    richTextReady.value = true;
    emit("ready");
  }
}

interface ParentApi {
  richTextContext?: unknown;
  addPluginReadyState: (name: string) => void;
  updatePluginReadyState: (name: string) => void;
  tooltipLoaded: (id: string) => void;
}

const parentApi: ParentApi = {
  richTextContext: undefined,
  addPluginReadyState,
  updatePluginReadyState,
  tooltipLoaded,
};
</script>
